import { useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from '../msalConfig';
import {
  listNotebooks,
  listSections,
  listPages,
  createPage,
  deletePage,
  searchPages,
} from '../oneNoteApi';

const ALLOWED_EMAILS = ['marc@defineholy.com', 'marc@myfiginsurance.com'];

export default function OneNoteTest() {
  const { instance, accounts } = useMsal();

  const userEmail = (accounts[0]?.username || '').toLowerCase();

  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const res = await instance.acquireTokenSilent({ ...graphRequest, account });
    return res.accessToken;
  }, [instance, accounts]);

  const [notebooks, setNotebooks] = useState(null);
  const [notebookId, setNotebookId] = useState('');
  const [sections, setSections] = useState(null);
  const [sectionId, setSectionId] = useState('');
  const [pages, setPages] = useState(null);
  const [pageTitle, setPageTitle] = useState('');
  const [createResult, setCreateResult] = useState(null);
  const [deletePageId, setDeletePageId] = useState('');
  const [deleteResult, setDeleteResult] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState(null);

  async function run(label, fn) {
    setBusy(label);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  }

  if (!ALLOWED_EMAILS.includes(userEmail)) {
    return <p style={{ padding: 24 }}>Not authorized.</p>;
  }

  return (
    <div style={{ padding: 24, fontFamily: 'monospace', maxWidth: 900 }}>
      <h2>OneNote API Test Surface</h2>
      <p style={{ color: '#888', fontSize: 12 }}>Dev tool — not linked from any nav. Signed in as: {userEmail}</p>
      {error && <pre style={{ color: 'red', whiteSpace: 'pre-wrap' }}>ERROR: {error}</pre>}
      {busy && <p style={{ color: '#888' }}>Working: {busy}…</p>}

      <hr />

      {/* LIST NOTEBOOKS */}
      <section>
        <h3>List Notebooks</h3>
        <button
          disabled={!!busy}
          onClick={() => run('listNotebooks', async () => {
            const token = await getToken();
            const data = await listNotebooks(token);
            setNotebooks(data);
          })}
        >
          List notebooks
        </button>
        {notebooks && (
          <pre style={{ background: '#f4f4f4', padding: 12, overflow: 'auto', fontSize: 11 }}>
            {JSON.stringify(notebooks, null, 2)}
          </pre>
        )}
      </section>

      <hr />

      {/* LIST SECTIONS */}
      <section>
        <h3>List Sections</h3>
        <input
          placeholder="Notebook ID"
          value={notebookId}
          onChange={e => setNotebookId(e.target.value)}
          style={{ width: 420, marginRight: 8 }}
        />
        <button
          disabled={!!busy || !notebookId.trim()}
          onClick={() => run('listSections', async () => {
            const token = await getToken();
            const data = await listSections(token, notebookId.trim());
            setSections(data);
          })}
        >
          List sections
        </button>
        {sections && (
          <pre style={{ background: '#f4f4f4', padding: 12, overflow: 'auto', fontSize: 11 }}>
            {JSON.stringify(sections, null, 2)}
          </pre>
        )}
      </section>

      <hr />

      {/* LIST PAGES */}
      <section>
        <h3>List Pages</h3>
        <input
          placeholder="Section ID"
          value={sectionId}
          onChange={e => setSectionId(e.target.value)}
          style={{ width: 420, marginRight: 8 }}
        />
        <button
          disabled={!!busy || !sectionId.trim()}
          onClick={() => run('listPages', async () => {
            const token = await getToken();
            const data = await listPages(token, sectionId.trim());
            setPages(data);
          })}
        >
          List pages
        </button>
        {pages && (
          <pre style={{ background: '#f4f4f4', padding: 12, overflow: 'auto', fontSize: 11 }}>
            {JSON.stringify(pages, null, 2)}
          </pre>
        )}
      </section>

      <hr />

      {/* CREATE TEST PAGE */}
      <section>
        <h3>Create Test Page</h3>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input
            placeholder="Section ID"
            value={sectionId}
            onChange={e => setSectionId(e.target.value)}
            style={{ width: 420 }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input
            placeholder="Page title"
            value={pageTitle}
            onChange={e => setPageTitle(e.target.value)}
            style={{ width: 280 }}
          />
          <button
            disabled={!!busy || !sectionId.trim() || !pageTitle.trim()}
            onClick={() => run('createPage', async () => {
              const token = await getToken();
              const html = `<!DOCTYPE html><html><head><title>${pageTitle}</title></head><body><p>Test page from DefineHoly platform.</p><p><a href="https://defineholy.com">Back to platform</a></p></body></html>`;
              const data = await createPage(token, sectionId.trim(), html);
              setCreateResult(data);
            })}
          >
            Create test page
          </button>
        </div>
        {createResult && (
          <>
            <pre style={{ background: '#f4f4f4', padding: 12, overflow: 'auto', fontSize: 11 }}>
              {JSON.stringify(createResult, null, 2)}
            </pre>
            {createResult.links?.oneNoteClientUrl?.href && (
              <p>
                <a href={createResult.links.oneNoteClientUrl.href} target="_blank" rel="noreferrer">
                  Open in OneNote desktop
                </a>
                {' · '}
                <a href={createResult.links.oneNoteWebUrl.href} target="_blank" rel="noreferrer">
                  Open in OneNote web
                </a>
              </p>
            )}
          </>
        )}
      </section>

      <hr />

      {/* DELETE PAGE */}
      <section>
        <h3>Delete Page</h3>
        <input
          placeholder="Page ID"
          value={deletePageId}
          onChange={e => setDeletePageId(e.target.value)}
          style={{ width: 420, marginRight: 8 }}
        />
        <button
          disabled={!!busy || !deletePageId.trim()}
          onClick={() => run('deletePage', async () => {
            const token = await getToken();
            const status = await deletePage(token, deletePageId.trim());
            setDeleteResult({ status });
          })}
        >
          Delete page
        </button>
        {deleteResult && (
          <pre style={{ background: '#f4f4f4', padding: 12, fontSize: 11 }}>
            {JSON.stringify(deleteResult, null, 2)}
          </pre>
        )}
      </section>

      <hr />

      {/* SEARCH */}
      <section>
        <h3>Search Pages</h3>
        <input
          placeholder="Search query"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{ width: 280, marginRight: 8 }}
        />
        <button
          disabled={!!busy || !searchQuery.trim()}
          onClick={() => run('searchPages', async () => {
            const token = await getToken();
            const data = await searchPages(token, searchQuery.trim());
            setSearchResults(data);
          })}
        >
          Search
        </button>
        {searchResults && (
          <pre style={{ background: '#f4f4f4', padding: 12, overflow: 'auto', fontSize: 11 }}>
            {JSON.stringify(searchResults, null, 2)}
          </pre>
        )}
      </section>
    </div>
  );
}
